import { AppError, isPickerCancel, toAppError } from "../core/errors";

export interface DisplayCapture {
  stream: MediaStream;
  label: string;
  hasSystemAudio: boolean;
}

/**
 * One picker per screen. Chrome allows several concurrent display captures
 * (each with its own gesture and picker), which is what makes multi-screen
 * recording possible without a special permission.
 */
export async function captureDisplay(options: {
  fps: number;
  wantSystemAudio: boolean;
}): Promise<DisplayCapture> {
  if (!navigator.mediaDevices?.getDisplayMedia) {
    throw new AppError("unsupported", "This browser can’t share a screen.");
  }
  const constraints: DisplayCaptureOptions = {
    video: {
      frameRate: { ideal: options.fps, max: options.fps },
      width: { ideal: 2560 },
      height: { ideal: 1600 },
    },
    audio: options.wantSystemAudio
      ? {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        }
      : false,
    // Chromium hints: only these enum values are accepted, and anything else
    // throws before the picker opens.
    systemAudio: options.wantSystemAudio ? "include" : "exclude",
    // Never offer Grabi's own tab: recording it is a hall of mirrors.
    selfBrowserSurface: "exclude",
    surfaceSwitching: "exclude",
  };

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia(constraints);
  } catch (error) {
    if (isPickerCancel(error)) {
      throw new AppError("interrupted", "Screen picker closed without choosing.");
    }
    throw toAppError(error, "permission");
  }

  const videoTrack = stream.getVideoTracks()[0];
  if (!videoTrack) {
    stopStream(stream);
    throw new AppError("permission", "The picker returned no video.");
  }
  videoTrack.contentHint = "detail";

  const hasSystemAudio = stream.getAudioTracks().length > 0;
  // Chrome's track label is `screen:11:0`; the surface is what a person
  // can actually tell apart when several are being recorded.
  const surface = videoTrack.getSettings().displaySurface;
  const label = surface === "window" ? "Window" : surface === "browser" ? "Tab" : "Screen";
  return { stream, label, hasSystemAudio };
}

export async function captureCamera(deviceId?: string): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: {
        ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30, max: 30 },
      },
      audio: false,
    });
  } catch (error) {
    throw toAppError(error, "camera");
  }
}

export async function captureMicrophone(deviceId?: string): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: {
        ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    });
  } catch (error) {
    throw toAppError(error, "microphone");
  }
}

export function stopStream(stream: MediaStream | null | undefined): void {
  if (!stream) return;
  for (const track of stream.getTracks()) {
    try {
      track.stop();
    } catch {
      // Already ended.
    }
  }
}

export function stopTrack(track: MediaStreamTrack | null | undefined): void {
  if (!track) return;
  try {
    track.stop();
  } catch {
    // Already ended.
  }
}
